import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import * as RadixDialog from '@radix-ui/react-dialog';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Activity,
  ArrowLeft,
  Box,
  Camera,
  CalendarClock,
  Clock,
  Globe,
  Hash,
  Home,
  KeyRound,
  MapPin,
  Pencil,
  Plus,
  Receipt,
  Save,
  Server,
  Tag,
  Trash2,
  Wifi,
  X,
} from 'lucide-react';
import {
  Buton,
  Etiket,
  Girdi,
  Iskelet,
  Kart,
  KartBaslik,
  KartIcerik,
  OnayDialog,
  Rozet,
  Tablar,
  toast,
  type RozetTonu,
} from '@emanetx/ui';
import { useCihazAnahtarlari, useCihazAnahtariIptal } from '../cihazlar/api.js';
import { AnahtarOlusturDialog } from '../cihazlar/anahtar-olustur-dialog.js';
import {
  noktaGuncelleIstegi,
  type NoktaDurumu,
  type NoktaGuncelleIstegi,
} from '@emanetx/shared-types';
import type { Nokta } from '@emanetx/sdk';
import { OlaylarTablosu } from '../cihazlar/olaylar-tablosu.js';
import { DolapDetayPenceresi } from '../dolaplar/dolap-detay-penceresi.js';
import { silmeHatasiniFormatla } from '../silme-hata.js';
import { NoktaDolaplarPaneli } from './nokta-dolaplar-paneli.js';
import { NoktaMuhasebePaneli } from '../muhasebe/nokta-muhasebe-paneli.js';
import { useNokta, useNoktaGuncelle, useNoktaSil } from './api.js';

const DURUM_TONU: Record<NoktaDurumu, RozetTonu> = {
  KURULUM: 'turuncu',
  AKTIF: 'yesil',
  BAKIM: 'turuncu',
  PASIF: 'gri',
};

const DURUM_ETIKET: Record<NoktaDurumu, string> = {
  KURULUM: 'Kurulum',
  AKTIF: 'Aktif',
  BAKIM: 'Bakım',
  PASIF: 'Pasif',
};

type Tab = 'genel' | 'raspi' | 'dolaplar' | 'olaylar' | 'muhasebe';
const TABLAR_GECERLI: Tab[] = ['genel', 'raspi', 'dolaplar', 'olaylar', 'muhasebe'];

interface DetaySearch {
  tab?: Tab;
  dolap?: string;
}

export function SayfaNoktaDetay() {
  const { id } = useParams({ strict: false }) as { id: string };
  const search = useSearch({ strict: false }) as DetaySearch;
  const navigate = useNavigate();
  const sorgu = useNokta(id);
  const sil = useNoktaSil();
  const [silOnay, setSilOnay] = useState(false);

  const aktifTab: Tab = TABLAR_GECERLI.includes(search.tab as Tab) ? (search.tab as Tab) : 'genel';
  const acikDolapId = search.dolap;

  const tabDegistir = (yeni: Tab) => {
    void navigate({
      to: '/panel/noktalar/$id',
      params: { id },
      search: { ...search, tab: yeni === 'genel' ? undefined : yeni },
    });
  };

  const dolapKapat = () => {
    void navigate({
      to: '/panel/noktalar/$id',
      params: { id },
      search: { ...search, dolap: undefined },
    });
  };

  if (sorgu.isLoading) {
    return (
      <div className="space-y-4">
        <Iskelet className="h-10 w-1/3" />
        <Iskelet className="h-40" />
      </div>
    );
  }

  if (sorgu.isError || !sorgu.data) {
    return (
      <div className="border-fume-200 dark:border-fume-800 dark:bg-fume-900 rounded-xl border bg-white p-6 text-center">
        <p className="text-fume-600 dark:text-fume-300 text-sm">Nokta bulunamadı.</p>
        <Buton
          stil="cizgili"
          boyut="kucuk"
          className="mt-4"
          onClick={() => navigate({ to: '/panel/noktalar' })}
        >
          <ArrowLeft size={14} /> Listeye dön
        </Buton>
      </div>
    );
  }

  const n = sorgu.data;

  return (
    <div className="space-y-6">
      <Link
        to="/panel/noktalar"
        className="text-fume-500 hover:text-fume-800 dark:hover:text-fume-100 inline-flex items-center gap-1 text-sm"
      >
        <ArrowLeft size={14} /> Noktalar
      </Link>

      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">{n.ad}</h1>
            <Rozet ton={DURUM_TONU[n.durum]} nokta>
              {DURUM_ETIKET[n.durum]}
            </Rozet>
          </div>
          <p className="text-fume-500 mt-1 font-mono text-xs">{n.kod}</p>
        </div>
        <Buton stil="tehlike" onClick={() => setSilOnay(true)}>
          <Trash2 size={14} /> Sil
        </Buton>
      </header>

      <Tablar
        ogeler={[
          {
            deger: 'genel',
            etiket: (
              <span className="flex items-center gap-1.5">
                <Home size={14} />
                Genel
              </span>
            ),
          },
          {
            deger: 'raspi',
            etiket: (
              <span className="flex items-center gap-1.5">
                <Server size={14} />
                Raspberry Pi
              </span>
            ),
          },
          {
            deger: 'dolaplar',
            etiket: (
              <span className="flex items-center gap-1.5">
                <Box size={14} />
                Dolaplar
              </span>
            ),
            sayac: n.dolapSayisi,
          },
          {
            deger: 'olaylar',
            etiket: (
              <span className="flex items-center gap-1.5">
                <Activity size={14} />
                Olaylar
              </span>
            ),
          },
          {
            deger: 'muhasebe',
            etiket: (
              <span className="flex items-center gap-1.5">
                <Receipt size={14} />
                Muhasebe
              </span>
            ),
          },
        ]}
        aktif={aktifTab}
        onAktifDegisti={(d) => tabDegistir(d as Tab)}
      />

      {aktifTab === 'genel' && <GenelTab nokta={n} />}
      {aktifTab === 'raspi' && <RaspiTab nokta={n} />}
      {aktifTab === 'dolaplar' && (
        <NoktaDolaplarPaneli noktaId={n.id} noktaKod={n.kod} noktaAd={n.ad} />
      )}
      {aktifTab === 'olaylar' && <OlaylarTablosu noktaId={n.id} kompakt />}
      {aktifTab === 'muhasebe' && (
        <NoktaMuhasebePaneli noktaId={n.id} noktaKod={n.kod} noktaAd={n.ad} />
      )}

      <DolapDetayPenceresi dolapId={acikDolapId} onKapat={dolapKapat} />

      <OnayDialog
        acik={silOnay}
        onDegisim={setSilOnay}
        baslik={`${n.kod} noktasını sil?`}
        aciklama="Bu nokta ve bağlı tüm dolaplar silinecek. Aktif kiralama veya geçmiş kayıt varsa silme engellenir; o durumda nokta durumunu PASIF yapın."
        onayMetni="Evet, sil"
        iptalMetni="Vazgeç"
        tur="tehlike"
        onOnay={async () => {
          try {
            await sil.mutateAsync(n.id);
            toast.success('Nokta silindi');
            void navigate({ to: '/panel/noktalar' });
          } catch (err) {
            toast.error(silmeHatasiniFormatla(err), { duration: 6000 });
          }
        }}
      />
    </div>
  );
}

function GenelTab({ nokta: n }: { nokta: Nokta }) {
  const guncelle = useNoktaGuncelle(n.id);
  const [duzenle, setDuzenle] = useState(false);

  if (duzenle) {
    return (
      <DuzenleFormu
        baslangic={{
          ad: n.ad,
          grupAdi: n.grupAdi ?? undefined,
          adres: n.adres ?? undefined,
          enlem: n.enlem ?? undefined,
          boylam: n.boylam ?? undefined,
          saatDilimi: n.saatDilimi,
          paraBirimi: n.paraBirimi,
          durum: n.durum,
          fotografCekimi: n.fotografCekimi,
          rezervasyonAktif: n.rezervasyonAktif,
        }}
        calisiyor={guncelle.isPending}
        onIptal={() => setDuzenle(false)}
        onKaydet={async (v) => {
          try {
            await guncelle.mutateAsync(v);
            toast.success('Nokta güncellendi');
            setDuzenle(false);
          } catch (err) {
            toast.error((err as Error).message);
          }
        }}
      />
    );
  }

  return (
    <Kart>
      <KartBaslik className="flex items-center justify-between">
        <h2 className="text-base font-semibold">Genel Bilgiler</h2>
        <Buton stil="cizgili" boyut="kucuk" onClick={() => setDuzenle(true)}>
          <Pencil size={13} /> Düzenle
        </Buton>
      </KartBaslik>
      <KartIcerik className="grid gap-3 sm:grid-cols-2">
        <Alan ikon={Hash} etiket="Kod" deger={<span className="font-mono">{n.kod}</span>} />
        <Alan ikon={Tag} etiket="Grup" deger={n.grupAdi ?? '—'} />
        <Alan ikon={MapPin} etiket="Adres" deger={n.adres ?? '—'} className="sm:col-span-2" />
        <Alan
          ikon={Globe}
          etiket="Koordinat"
          deger={
            n.enlem !== null && n.enlem !== undefined && n.boylam !== null && n.boylam !== undefined
              ? `${n.enlem.toFixed(6)}, ${n.boylam.toFixed(6)}`
              : '—'
          }
        />
        <Alan ikon={Clock} etiket="Saat dilimi" deger={n.saatDilimi} />
        <Alan
          ikon={Tag}
          etiket="Para birimi"
          deger={<span className="font-mono">{n.paraBirimi}</span>}
        />
        <Alan
          ikon={CalendarClock}
          etiket="Eklenme"
          deger={new Date(n.olusturmaTarihi).toLocaleString('tr-TR', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })}
        />
      </KartIcerik>
    </Kart>
  );
}

function RaspiTab({ nokta: n }: { nokta: Nokta }) {
  return (
    <div className="space-y-4">
      <div className="grid gap-4 lg:grid-cols-2">
        <Kart>
          <KartBaslik>
            <h2 className="text-base font-semibold">Raspberry Pi</h2>
          </KartBaslik>
          <KartIcerik className="space-y-3">
            <Alan
              ikon={Server}
              etiket="Cihaz ID (raspi seri no)"
              deger={n.raspiSeriNo ?? <span className="text-fume-400">Henüz bağlanmadı</span>}
            />
            <Alan
              ikon={Wifi}
              etiket="Son bağlantı IP"
              deger={n.raspiIpAdresi ?? <span className="text-fume-400">—</span>}
            />
            <Alan
              ikon={CalendarClock}
              etiket="Son ping"
              deger={
                n.raspiSonPing ? (
                  new Date(n.raspiSonPing).toLocaleString('tr-TR')
                ) : (
                  <span className="text-fume-400">—</span>
                )
              }
            />
          </KartIcerik>
        </Kart>

        <Kart>
          <KartBaslik>
            <h2 className="text-base font-semibold">Özellikler</h2>
          </KartBaslik>
          <KartIcerik className="space-y-2">
            <Ozellik ikon={Camera} etiket="Dolap içi fotoğraf" aktif={n.fotografCekimi} />
            <Ozellik ikon={CalendarClock} etiket="Rezervasyon" aktif={n.rezervasyonAktif} />
          </KartIcerik>
        </Kart>
      </div>

      <CihazAnahtarlariKart noktaId={n.id} noktaKod={n.kod} noktaAd={n.ad} />
    </div>
  );
}

function CihazAnahtarlariKart({
  noktaId,
  noktaKod,
  noktaAd,
}: {
  noktaId: string;
  noktaKod: string;
  noktaAd: string;
}) {
  const [uretAcik, setUretAcik] = useState(false);
  const [iptalId, setIptalId] = useState<string | null>(null);
  const [iptalSebep, setIptalSebep] = useState('');
  const liste = useCihazAnahtarlari(noktaId);
  const iptal = useCihazAnahtariIptal(noktaId);

  const iptalEt = async () => {
    if (!iptalId || iptalSebep.trim().length < 3) return;
    try {
      await iptal.mutateAsync({ id: iptalId, sebep: iptalSebep.trim() });
      toast.success('Anahtar iptal edildi');
      setIptalId(null);
      setIptalSebep('');
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const kayitlar = liste.data ?? [];
  const aktifSayi = kayitlar.filter((k) => k.aktif).length;

  return (
    <Kart>
      <KartBaslik>
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold">Raspi API Anahtarları</h2>
            <p className="text-fume-500 text-xs">
              Raspi'nin sunucuya bağlanması için. {aktifSayi} aktif anahtar.
            </p>
          </div>
          <Buton boyut="kucuk" onClick={() => setUretAcik(true)}>
            <Plus size={13} /> Yeni Anahtar
          </Buton>
        </div>
      </KartBaslik>
      <KartIcerik>
        {liste.isLoading ? (
          <p className="text-fume-500 py-4 text-center text-xs">Yükleniyor…</p>
        ) : kayitlar.length === 0 ? (
          <div className="border-fume-200 dark:border-fume-700 flex flex-col items-center gap-2 rounded-lg border border-dashed py-8 text-center">
            <KeyRound size={20} className="text-fume-400" />
            <p className="text-fume-600 dark:text-fume-300 text-sm font-medium">
              Henüz API anahtarı yok
            </p>
            <p className="text-fume-500 text-xs">
              Raspi'nin sunucuya bağlanması için bir anahtar üretmelisin.
            </p>
            <Buton boyut="kucuk" stil="cizgili" onClick={() => setUretAcik(true)}>
              <Plus size={13} /> Anahtar Üret
            </Buton>
          </div>
        ) : (
          <ul className="divide-fume-100 dark:divide-fume-800 divide-y">
            {kayitlar.map((k) => (
              <li key={k.id} className="flex items-center gap-3 py-3">
                <KeyRound size={16} className={k.aktif ? 'text-marka-500' : 'text-fume-400'} />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-fume-900 dark:text-fume-50 text-sm font-medium">
                      {k.ad}
                    </span>
                    {k.aktif ? (
                      <Rozet ton="yesil" className="text-[10px]">
                        Aktif
                      </Rozet>
                    ) : (
                      <Rozet ton="kirmizi" className="text-[10px]">
                        İptal
                      </Rozet>
                    )}
                  </div>
                  <div className="text-fume-500 mt-0.5 flex flex-wrap items-center gap-2 text-[11px]">
                    <code className="font-mono">{k.cihazId}</code>
                    <span>·</span>
                    <span>
                      …<code className="font-mono">{k.sonDort}</code>
                    </span>
                    {k.sonKullanim && (
                      <>
                        <span>·</span>
                        <span>Son: {new Date(k.sonKullanim).toLocaleString('tr-TR')}</span>
                      </>
                    )}
                    {k.sonBaglantiIp && (
                      <>
                        <span>·</span>
                        <span className="font-mono">{k.sonBaglantiIp}</span>
                      </>
                    )}
                  </div>
                  {!k.aktif && k.iptalSebebi && (
                    <p className="text-fume-400 mt-1 text-[11px] italic">
                      İptal sebebi: {k.iptalSebebi}
                    </p>
                  )}
                </div>
                {k.aktif && (
                  <button
                    onClick={() => setIptalId(k.id)}
                    className="text-fume-400 px-2 text-xs font-medium hover:text-red-600"
                  >
                    İptal
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </KartIcerik>

      <AnahtarOlusturDialog
        acik={uretAcik}
        onDegisim={setUretAcik}
        noktaId={noktaId}
        noktaKod={noktaKod}
        noktaAd={noktaAd}
      />

      <AnahtarIptalDialog
        acik={iptalId !== null}
        onKapat={() => {
          setIptalId(null);
          setIptalSebep('');
        }}
        sebep={iptalSebep}
        onSebep={setIptalSebep}
        bekliyor={iptal.isPending}
        onOnay={() => void iptalEt()}
      />
    </Kart>
  );
}

function AnahtarIptalDialog({
  acik,
  onKapat,
  sebep,
  onSebep,
  bekliyor,
  onOnay,
}: {
  acik: boolean;
  onKapat: () => void;
  sebep: string;
  onSebep: (v: string) => void;
  bekliyor: boolean;
  onOnay: () => void;
}) {
  return (
    <RadixDialog.Root open={acik} onOpenChange={(v) => !v && onKapat()}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="bg-fume-900/50 fixed inset-0 z-40 backdrop-blur-sm" />
        <RadixDialog.Content className="dark:bg-fume-900 fixed left-1/2 top-1/2 z-50 w-[94vw] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white p-6 shadow-2xl">
          <RadixDialog.Title className="text-base font-semibold text-red-600 dark:text-red-400">
            API Anahtarını İptal Et
          </RadixDialog.Title>
          <RadixDialog.Description className="text-fume-500 dark:text-fume-400 mt-1.5 text-sm">
            Bu anahtarla bağlı Raspi anında bağlantıyı kaybeder. Geri alınamaz.
          </RadixDialog.Description>
          <div className="mt-5 space-y-1.5">
            <Etiket htmlFor="iptal-sebep">Sebep (denetim için)</Etiket>
            <Girdi
              id="iptal-sebep"
              placeholder="Örn: Cihaz değiştirildi"
              value={sebep}
              onChange={(e) => onSebep(e.target.value)}
              maxLength={500}
            />
          </div>
          <div className="flex justify-end gap-2 pt-5">
            <Buton stil="cizgili" onClick={onKapat} disabled={bekliyor}>
              Vazgeç
            </Buton>
            <Buton
              onClick={onOnay}
              disabled={bekliyor || sebep.trim().length < 3}
              className="bg-red-600 hover:bg-red-700"
            >
              {bekliyor ? 'İptal ediliyor…' : 'İptal Et'}
            </Buton>
          </div>
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

function Alan({
  ikon: Ikon,
  etiket,
  deger,
  className,
}: {
  ikon: React.ComponentType<{ size?: number; className?: string }>;
  etiket: string;
  deger: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`flex items-start gap-3 ${className ?? ''}`}>
      <Ikon size={16} className="text-fume-400 mt-0.5 flex-shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-fume-500 text-xs">{etiket}</p>
        <div className="text-fume-800 dark:text-fume-100 break-words text-sm">{deger}</div>
      </div>
    </div>
  );
}

function Ozellik({
  ikon: Ikon,
  etiket,
  aktif,
}: {
  ikon: React.ComponentType<{ size?: number; className?: string }>;
  etiket: string;
  aktif: boolean;
}) {
  return (
    <div className="flex items-center justify-between">
      <div className="text-fume-700 dark:text-fume-200 flex items-center gap-2 text-sm">
        <Ikon size={16} className="text-fume-400" />
        {etiket}
      </div>
      <Rozet ton={aktif ? 'yesil' : 'gri'} nokta>
        {aktif ? 'Açık' : 'Kapalı'}
      </Rozet>
    </div>
  );
}

function DuzenleFormu({
  baslangic,
  calisiyor,
  onIptal,
  onKaydet,
}: {
  baslangic: NoktaGuncelleIstegi;
  calisiyor: boolean;
  onIptal: () => void;
  onKaydet: (v: NoktaGuncelleIstegi) => Promise<void>;
}) {
  const form = useForm<NoktaGuncelleIstegi>({
    resolver: zodResolver(noktaGuncelleIstegi),
    defaultValues: baslangic,
  });

  useEffect(() => {
    form.reset(baslangic);
  }, [baslangic, form]);

  const gonder = form.handleSubmit(async (v) => {
    await onKaydet(v);
  });

  return (
    <form onSubmit={gonder}>
      <Kart>
        <KartBaslik className="flex items-center justify-between">
          <h2 className="text-base font-semibold">Bilgileri düzenle</h2>
          <Buton stil="sade" boyut="kucuk" onClick={onIptal} type="button">
            <X size={13} /> İptal
          </Buton>
        </KartBaslik>
        <KartIcerik className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Etiket htmlFor="ad">Ad</Etiket>
              <Girdi id="ad" {...form.register('ad')} />
            </div>
            <div className="space-y-1.5">
              <Etiket htmlFor="grupAdi">Grup adı</Etiket>
              <Girdi id="grupAdi" {...form.register('grupAdi')} />
            </div>
          </div>

          <div className="space-y-1.5">
            <Etiket htmlFor="adres">Adres</Etiket>
            <Girdi id="adres" {...form.register('adres')} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Etiket htmlFor="enlem">Enlem</Etiket>
              <Girdi
                id="enlem"
                type="number"
                step="any"
                {...form.register('enlem', { valueAsNumber: true })}
              />
            </div>
            <div className="space-y-1.5">
              <Etiket htmlFor="boylam">Boylam</Etiket>
              <Girdi
                id="boylam"
                type="number"
                step="any"
                {...form.register('boylam', { valueAsNumber: true })}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Etiket htmlFor="paraBirimi">Para birimi</Etiket>
              <select
                id="paraBirimi"
                className="border-fume-200 focus:ring-marka-500 dark:border-fume-700 dark:bg-fume-900 dark:text-fume-50 h-11 w-full rounded-lg border bg-white px-3 text-sm focus:outline-none focus:ring-2"
                {...form.register('paraBirimi')}
              >
                <option value="TRY">₺ TRY</option>
                <option value="EUR">€ EUR</option>
                <option value="USD">$ USD</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <Etiket htmlFor="durum">Durum</Etiket>
              <select
                id="durum"
                className="border-fume-200 focus:ring-marka-500 dark:border-fume-700 dark:bg-fume-900 dark:text-fume-50 h-11 w-full rounded-lg border bg-white px-3 text-sm focus:outline-none focus:ring-2"
                {...form.register('durum')}
              >
                <option value="KURULUM">Kurulum</option>
                <option value="AKTIF">Aktif</option>
                <option value="BAKIM">Bakım</option>
                <option value="PASIF">Pasif</option>
              </select>
            </div>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row">
            <label className="border-fume-200 dark:border-fume-700 flex flex-1 items-center gap-2 rounded-lg border px-3 py-2">
              <input type="checkbox" {...form.register('fotografCekimi')} />
              <span className="text-sm">Dolap içi fotoğraf çekimi</span>
            </label>
            <label className="border-fume-200 dark:border-fume-700 flex flex-1 items-center gap-2 rounded-lg border px-3 py-2">
              <input type="checkbox" {...form.register('rezervasyonAktif')} />
              <span className="text-sm">Rezervasyon aktif</span>
            </label>
          </div>
        </KartIcerik>
        <div className="border-fume-100 dark:border-fume-800 flex justify-end gap-2 border-t px-6 py-3">
          <Buton type="submit" disabled={calisiyor}>
            <Save size={14} /> {calisiyor ? 'Kaydediliyor...' : 'Kaydet'}
          </Buton>
        </div>
      </Kart>
    </form>
  );
}
